import * as React from 'react';
import PropTypes from 'prop-types';
import { useTheme } from '@emotion/react';
import Box from '@mui/material/Box';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import { tokens } from '../../global/theme/tokens';

const drawerBleeding = 56;
const noop = () => {};

function ExoDrawer(props) {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const {
    window,
    children,
    open,
    onClose,
    // SwipeableDrawer requires onOpen; without it a swipe from the edge throws.
    onOpen = noop,
    className,
    zIndex,
    sx,
    // MUI keeps a closed SwipeableDrawer mounted by default. Pass false when the
    // content is expensive, so it is unmounted while closed.
    keepMounted = true,
    // Swipe-to-open renders an invisible strip along the bottom edge on
    // Android, which swallows taps on whatever sits underneath (bottom menu,
    // floating buttons). None of our drawers is meant to be swiped open.
    disableSwipeToOpen = true,
    // Only a swipe that starts on the handle closes the drawer. Otherwise
    // scrolling up inside long content (an email) can close it by accident.
    handleOnly = false,
  } = props;

  // This is used only for the example
  const container =
    window !== undefined ? () => window().document.body : undefined;

  return (
    <Box className={className}>
      <SwipeableDrawer
        container={container}
        anchor="bottom"
        open={open}
        onClose={onClose}
        onOpen={onOpen}
        disableSwipeToOpen={disableSwipeToOpen}
        swipeAreaWidth={drawerBleeding}
        ModalProps={{ keepMounted }}
        sx={{
          zIndex,
          '& .MuiPaper-root': {
            bgcolor: 'transparent',
          },
          // was a <Global> rule that also resized every other drawer on screen
          '& > .MuiPaper-root': {
            height: '90%',
            overflow: 'visible',
          },
        }}
      >
        {/* the full-width strip around the pill is the touch target */}
        <Box
          className="w-full absolute top-0 flex justify-center pt-2"
          sx={{ height: handleOnly ? '28px' : undefined, zIndex: 10 }}
        >
          <Box
            className="h-[6px] w-[85px] rounded-full z-50 relative"
            sx={{ bgcolor: colors.selected, zIndex: 10 }}
          />
        </Box>
        <Box
          // MUI listens for the swipe on document; stopping touchstart here
          // keeps touches in the content from reaching it
          onTouchStart={handleOnly ? (e) => e.stopPropagation() : undefined}
          sx={{
            pt: handleOnly ? '28px' : 2.5,
            height: '100%',
            overflow: 'auto',
            overscrollBehavior: 'contain',
            bgcolor: colors.glass,
            ...sx,
          }}
          className="backdrop-blur-lg"
        >
          {children}
        </Box>
      </SwipeableDrawer>
    </Box>
  );
}

ExoDrawer.propTypes = {
  /**
   * Injected by the documentation to work in an iframe.
   * You won't need it on your project.
   */
  window: PropTypes.func,
  keepMounted: PropTypes.bool,
  handleOnly: PropTypes.bool,
  disableSwipeToOpen: PropTypes.bool,
  onOpen: PropTypes.func,
};

export default ExoDrawer;
